import { Pipe, PipeTransform } from '@angular/core';
import { Discount, DiscountType, Currency } from '@galaxy/billing';
import { formatDate, formatting } from './format-date.pipe';
import { formatDisplayPrice } from '@vendasta/shared';

/*
 * Format a discount for display.
 *
 * Usage:
 *   discount | formatDiscount
 * Examples:
 *   {{ { amount: 100, discountType: 'FIXED_AMOUNT', start: '2019-12-12' } | formatDiscount: 'USD' }}
 *   formats to: $1.00 off starting 2019-12-12
 *
 *   {{ { amount: 15, discountType: 'PERCENT', start: '2019-08-01' end: '2019-09-01' } | formatDiscount: 'USD' }}
 *   formats to: 15% off starting 2019-08-01 to 2019-09-01
 */
@Pipe({  name: 'formatDiscount' })
export class FormatDiscountPipe implements PipeTransform {
  transform(discount: Discount, currency: Currency): string {
    if (!discount) {
      return 'N/A';
    }

    const amount = formatDiscountAmount(discount.amount, discount.discountType, currency);

    let off = 'off';
    switch (discount.discountType) {
      case DiscountType.FIXED_NUMBER_OF_UNITS: {
        off = 'free units';
        if (discount.resetEachPeriod) {
          off += ' per period';
        }
        break;
      }
      case DiscountType.FIXED_AMOUNT_PER_UNIT: {
        off = 'off each unit';
        break;
      }
    }

    const start = formatDate(discount.start, formatting.date, '', true);
    const dateRange =
      discount.end.valueOf() !== new Date('0001-01-01T00:00:00Z').valueOf()
        ? `${start} to ${formatDate(discount.end, formatting.date, '', true)}`
        : start;

    return `${amount} ${off} starting ${dateRange}`;
  }
}

function formatDiscountAmount(amount: number, rule: DiscountType, currency: Currency): string {
  if ([DiscountType.FIXED_AMOUNT, DiscountType.FIXED_AMOUNT_PER_UNIT].includes(rule)) {
    return formatDisplayPrice(amount, currency, null, true, false, true, false);
  } else if (rule === DiscountType.PERCENT_AMOUNT) {
    return amount.toString() + '%';
  }
  return amount.toString();
}
